import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { ArrowRight, Target, Calendar, Sparkles, MessageCircle, CheckCircle, ChevronDown, ChevronUp, RotateCcw, Brain, TrendingUp, Heart } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { useAuth } from '@/context/AuthContext';
import SEO from '@/components/SEO';
import axios from 'axios';
import { toast } from 'sonner';
import { usePricing } from '@/hooks/usePricing';
import { EarlyAccessCountdown } from '@/components/EarlyAccessCountdown';

const API = `${window.location.origin}/api`;

const LOGO_NAV = "/images/dea-wordmark.png";
const LOGO_HERO = "/images/dea-wordmark-tagline.png";
const DEA_AVATAR = "/images/dea-portrait.png";
const DEON_AVATAR = "/images/deon-headshot.png";
const PRODUCT_IMAGE = "https://customer-assets.emergentagent.com/job_custom-gpt-clone/artifacts/yxz71gpc_AGPT%20Product%20Pictures%20%283%29.avif";

const features = [
  {
    icon: Target,
    title: 'Your 90-Day Plan',
    description: 'A personalized roadmap built around your goals, your life, and who you\'re becoming.'
  },
  {
    icon: Calendar,
    title: 'Daily Clarity & Direction',
    description: 'Every day Dea tells you exactly what to focus on, so you never wonder where to start.'
  },
  {
    icon: Sparkles,
    title: 'Cycle Syncing',
    description: 'Work with your body, not against it. Dea adjusts your plan around your natural rhythm.'
  },
  {
    icon: MessageCircle,
    title: 'A Coach Who Shows Up Daily',
    description: 'Dea is trained on 20+ years of real coaching wisdom, available 24/7, always in your corner.'
  }
];

const testimonials = [
  {
    quote: "I've only been working with AGPT for 1 hour, and I have already cried and found clarity. I feel so supported.",
    author: "Sophia",
    image: "/images/sophia.png"
  },
  {
    quote: "Dea has completely transformed my budget! That alone is worth every penny I invested — it's already given me a return on my investment!",
    author: "Kamila",
    image: "/images/kamila.png"
  },
  {
    quote: "Wait, Dea is going to align my daily tasks with my monthly cycle - so I can stop crashing out? That's wild!",
    author: "Amanda",
    image: "/images/amanda.png"
  }
];

const isThisYouItems = [
  "You have big goals, but no clear plan to actually reach them",
  "You're ready to grow in your mindset, your habits, AND your life",
  "You want daily clarity on exactly what to focus on",
  "You want to grow your business without sacrificing your well-being",
  "You know you're meant for more, and you just need the right coach to help you get there"
];

const faqItems = [
  {
    question: "Is this just ChatGPT?",
    answer: "No. Dea is trained specifically on Deon's 20+ years of coaching experience. She builds your personalized 90-day plan, tracks your cycle, gives you daily clarity, and checks in with you every day — things ChatGPT doesn't do."
  },
  {
    question: "Is my information secure?",
    answer: "Yes. Dea is built on secure infrastructure powered by OpenAI and Claude. Your conversations are private and encrypted."
  },
  {
    question: "What if I fall behind?",
    answer: "No guilt. No shame. Dea recalibrates your plan and keeps you moving forward. Life happens — she adjusts with you."
  },
  {
    question: "Do I need to know my cycle?",
    answer: "You'll enter it once during setup. Dea handles the rest — adjusting your tasks based on your energy levels throughout the month."
  },
  {
    question: "Is this a subscription?",
    answer: "No. This is a one-time payment for lifetime access to Dea. No monthly fees, and nothing to cancel."
  },
  {
    question: "Do I have to get confident before I can start?",
    answer: "No. You build your confidence and your business at the same time, with Dea guiding you. You don't wait until you feel like her. You become her by doing the work, one day at a time, starting exactly where you are today."
  },
  {
    question: "What happens after I buy?",
    answer: "You'll get instant access to your personal dashboard and can start chatting with Dea immediately. She'll walk you through setting up your 90-day goal."
  },
  {
    question: "Why do I have to start new conversations sometimes?",
    answer: "To keep Dea running smoothly, chat threads have a limit. Don't worry — Dea will let you know when you're reaching the end of a thread so you can start a fresh conversation without losing momentum."
  },
  {
    question: "Does Dea remember me?",
    answer: "Yes. Dea remembers your goals, your progress, and your previous conversations. You're not starting from scratch every time you log in."
  },
  {
    question: "Can I use this on my phone?",
    answer: "Yes. Dea works on desktop, tablet, and mobile. You can chat with Dea wherever you are."
  },
  {
    question: "What if I'm not tech-savvy?",
    answer: "No problem. If you can send a text message, you can use Dea. It's a simple chat interface — just talk to Dea like you'd talk to a friend or coach."
  },
  {
    question: "What if I don't have a business yet?",
    answer: "Dea isn't just for business. She helps you grow in every area — mindset, habits, goals, strategy, and well-being. Whether you're building a business, rebuilding yourself, or both, Dea meets you where you are."
  },
  {
    question: "Who do I contact for technical support?",
    answer: "Email us at info@TheAmbitionPlanner.com — we're here to help."
  },
  {
    question: "Is this available outside the US?",
    answer: "Yes. Dea is available worldwide. All you need is an internet connection."
  }
];

// FAQ Accordion Item Component
function FAQItem({ question, answer }) {
  const [isOpen, setIsOpen] = useState(false);
  
  return (
    <div className="border-b border-[#EAD8C2]">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="w-full py-5 flex items-center justify-between text-left hover:bg-[#EAD8C2]/10 transition-colors px-4 rounded-lg"
      >
        <span className="font-medium text-[#726158] pr-4">{question}</span>
        {isOpen ? (
          <ChevronUp className="h-5 w-5 text-[#AA9283] flex-shrink-0" />
        ) : (
          <ChevronDown className="h-5 w-5 text-[#AA9283] flex-shrink-0" />
        )}
      </button>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: 'auto' }}
          exit={{ opacity: 0, height: 0 }}
          className="pb-5 px-4"
        >
          <p className="text-[#AA9283]">{answer}</p>
        </motion.div>
      )}
    </div>
  );
}


// Glow Up Collective — Evergreen Offer
function LifetimeAccessSection({ isLifetimeMember }) {
  const navigate = useNavigate();
  const pricing = usePricing();
  const handleGetLifetime = async () => {
    const token = localStorage.getItem('token');
    if (!token) { navigate('/checkout?plan=lifetime'); return; }
    try {
      const res = await axios.get(`${API}/user/access-level`, { headers: { Authorization: `Bearer ${token}` } });
      const { hasAccess, type, showUpgrade } = res.data || {};
      if (hasAccess && type === 'full' && !showUpgrade) { toast.success("You're already a Lifetime Member \u{1F49B}"); navigate('/dashboard'); return; }
      navigate(showUpgrade ? '/checkout?upgrade=true' : '/checkout?plan=lifetime');
    } catch (err) { navigate('/checkout?plan=lifetime'); }
  };

  const includedItems = [
    { title: 'Full access to Dea', desc: 'trained by Deon Stokes to be your AI ambition coach, available 24/7' },
    { title: 'Your BECOMING HER Dashboard', desc: 'built around your goals, habits, and who you\u2019re becoming' },
    { title: 'Your Habit Tracker', desc: 'to build the daily routines that actually stick' },
    { title: 'Your Daily Journal', desc: 'to reflect, reset, and stay connected to your why' },
    { title: 'Your Weekly Check-in', desc: 'to measure your progress and adjust your plan' },
    { title: 'Support that shows up for you', desc: 'every single day' },
  ];

  if (isLifetimeMember) {
    return (
      <section className="py-20 sm:py-24" style={{ backgroundColor: '#3D3229' }} data-testid="lifetime-access-member">
        <div className="max-w-2xl mx-auto px-5 sm:px-6 text-center">
          <p className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-6" style={{ color: '#B8956A' }}>You{"\u2019"}re Already In</p>
          <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-6" style={{ color: '#FFFCF7' }}>You already have <span style={{ color: '#B8956A' }}>Lifetime Access</span> {"\u2713"}</h2>
          <p className="text-sm sm:text-base leading-relaxed mb-10 max-w-xl mx-auto" style={{ color: '#FFFCF7' }}>Your access is locked in for life. Nothing to renew, nothing to cancel, and you will never be charged again.</p>
          <Link to="/chat">
            <Button className="text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-6 rounded-full font-semibold" style={{ backgroundColor: '#B8956A', color: '#FFFCF7' }} data-testid="lifetime-member-go-chat">Continue with Dea {"\u2192"}</Button>
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="py-20 sm:py-24" style={{ backgroundColor: '#3D3229' }} data-testid="lifetime-access-section">
      <div className="max-w-2xl mx-auto px-5 sm:px-6 text-center">
        <p className="text-xs sm:text-sm tracking-[0.2em] uppercase mb-6" style={{ color: '#B8956A' }}>One Payment. Yours for Good.</p>
        <h2 className="font-serif text-3xl sm:text-4xl lg:text-5xl mb-6" style={{ color: '#FFFCF7' }}>Lifetime Access to <span style={{ color: '#B8956A' }}>Dea</span></h2>
        <p className="text-sm sm:text-base leading-relaxed mb-10 max-w-xl mx-auto" style={{ color: '#FFFCF7' }}>
          Get full, lifetime access to Dea, your personal AI ambition coach, for one payment. No monthly fees. Yours for good.
        </p>
        <div className="text-left max-w-lg mx-auto mb-10">
          {includedItems.map((item, i) => (
            <div key={i} className={`py-4 ${i < includedItems.length - 1 ? 'border-b border-white/10' : ''}`}>
              <div className="flex items-start gap-3">
                <CheckCircle className="h-5 w-5 shrink-0 mt-0.5" style={{ color: '#B8956A' }} />
                <p className="text-sm sm:text-base" style={{ color: '#FFFCF7' }}><strong>{item.title}</strong> {"\u2014"} {item.desc}</p>
              </div>
            </div>
          ))}
        </div>
        <div className="mb-8" data-testid="lifetime-pricing-block">
          {pricing.is_early_access && (
            <p className="text-xs tracking-[0.2em] uppercase mb-2" style={{ color: '#B8956A' }} data-testid="lifetime-early-access-label">{pricing.early_access_label}</p>
          )}
          <p className="text-5xl sm:text-6xl font-bold font-serif" style={{ color: '#B8956A' }} data-testid="lifetime-price">
            {pricing.is_early_access && (
              <span className="text-2xl sm:text-3xl line-through mr-3 align-middle" style={{ color: 'rgba(255,252,247,0.45)' }} data-testid="lifetime-regular-price">${pricing.lifetime_regular_price}</span>
            )}
            ${pricing.lifetime_price}<span className="text-2xl sm:text-3xl" style={{ color: 'rgba(184,149,106,0.85)' }}> one-time</span>
          </p>
          <p className="text-base mt-2" style={{ color: '#FFFCF7' }}>Lifetime access. One payment. Yours for good.</p>
        </div>
        <Button onClick={handleGetLifetime} className="text-base sm:text-lg px-6 sm:px-10 py-5 sm:py-6 rounded-full font-semibold max-w-full transition-all hover:brightness-110" style={{ backgroundColor: '#B8956A', color: '#FFFCF7' }} data-testid="lifetime-cta-btn">
          Get Lifetime Access
          <ArrowRight className="ml-2 h-5 w-5 shrink-0" />
        </Button>
        <p className="text-xs mt-5" style={{ color: 'rgba(255,252,247,0.6)' }}>One-time payment. No subscription. No recurring fees.</p>
        {pricing.is_early_access && (
          <p className="text-sm mt-3 font-medium" style={{ color: '#B8956A' }} data-testid="lifetime-urgency-line">{pricing.urgency_line}</p>
        )}
        {pricing.is_early_access && <EarlyAccessCountdown endAt={pricing.early_access_end_at} label={`Price returns to $${pricing.lifetime_regular_price} in`} />}
      </div>
    </section>
  );
}


// Interactive "Is This You?" Component
function IsThisYouSection({ isLifetimeMember = false }) {
  const [checkedItems, setCheckedItems] = useState({});
  const checkedCount = Object.values(checkedItems).filter(Boolean).length;
  
  const toggleItem = (index) => {
    setCheckedItems(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  return (
    <section className="py-24 bg-[#FFFCF7]">
      <div className="max-w-4xl mx-auto px-4 sm:px-6">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mb-12"
        >
          <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-2">
            Is This <span className="text-[#CAAB84]">You?</span>
          </h2>
          <p className="text-[#AA9283] text-lg">Click All That Apply</p>
        </motion.div>
        
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="glass-card rounded-2xl p-8"
        >
          <div className="space-y-4">
            {isThisYouItems.map((item, index) => (
              <label
                key={index}
                className="flex items-start gap-4 cursor-pointer group"
                onClick={() => toggleItem(index)}
              >
                <div className={`w-6 h-6 rounded-md border-2 flex items-center justify-center flex-shrink-0 mt-0.5 transition-all ${
                  checkedItems[index] 
                    ? 'bg-[#87A6A7] border-[#87A6A7]' 
                    : 'border-[#AA9283] group-hover:border-[#87A6A7]'
                }`}>
                  {checkedItems[index] && (
                    <CheckCircle className="h-4 w-4 text-white" />
                  )}
                </div>
                <span className={`text-lg transition-colors ${
                  checkedItems[index] ? 'text-[#726158]' : 'text-[#AA9283]'
                }`}>
                  {item}
                </span>
              </label>
            ))}
          </div>
          
          {/* Show message when 1+ items checked */}
          {checkedCount > 0 && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mt-8 pt-8 border-t border-[#EAD8C2] text-center"
            >
              <p className="text-2xl font-serif text-[#726158] mb-6">
                ✨ Dea was built for you.
              </p>
              {isLifetimeMember ? (
                <Link to="/chat">
                  <Button className="btn-gold text-lg px-8 py-6">
                    Continue with Dea
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              ) : (
                <Link to="/checkout?plan=lifetime">
                  <Button className="btn-gold text-lg px-8 py-6">
                    Let's Get To Work Sis
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              )}
            </motion.div>
          )}
        </motion.div>
      </div>
    </section>
  );
}

export default function Landing() {
  const pricing = usePricing();
  const { user } = useAuth();
  const [isLifetimeMember, setIsLifetimeMember] = useState(false);

  // Check the authoritative server-side access level for the current user.
  // Legacy lifetime members (accessType === 'full') must never see the new
  // $37/month CTAs — they're locked in forever. We rely on /user/access-level
  // because the cached `user` object may be stale post-purchase.
  useEffect(() => {
    let cancelled = false;
    const token = localStorage.getItem('token');
    if (!token) {
      setIsLifetimeMember(false);
      return undefined;
    }
    axios
      .get(`${API}/user/access-level`, { headers: { Authorization: `Bearer ${token}` } })
      .then((res) => {
        if (cancelled) return;
        const { hasAccess, type, showUpgrade } = res.data || {};
        setIsLifetimeMember(Boolean(hasAccess && type === 'full' && !showUpgrade));
      })
      .catch(() => {
        if (!cancelled) setIsLifetimeMember(false);
      });
    return () => {
      cancelled = true;
    };
  }, [user]);

  return (
    <>
      <SEO 
        title="Dea | Your AI Ambition Coach for Women of Faith"
        description="Dea is an AI Ambition Coach for Women of Faith. Meet Dea, your personalized AI coach for clarity, habits, mindset, strategy, and well-being. Build the business and life you've been dreaming of."
      />
      <div className="min-h-screen bg-[#FFFCF7] overflow-x-hidden">
      {/* Nav */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center">
            <img src={LOGO_NAV} alt="Dea" className="h-8 sm:h-10 w-auto object-contain" />
          </Link>
          <div className="flex items-center gap-2 sm:gap-4">
            {user ? (
              <Link to="/chat">
                <Button className="btn-gold" data-testid="nav-chat-btn">
                  Go to Chat
                </Button>
              </Link>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="ghost" className="text-[#AA9283] hover:text-[#726158]" data-testid="nav-login-btn">
                    Login
                  </Button>
                </Link>
                <Link to="/signup">
                  <Button className="btn-gold" data-testid="nav-signup-btn">
                    Get Started
                  </Button>
                </Link>
              </>
            )}
          </div>
        </div>
      </nav>

      {/* Hero - Split Layout */}
      <section className="pt-16 sm:pt-20 pb-0 relative overflow-hidden" style={{ backgroundColor: '#FFFCF7' }}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid lg:grid-cols-2 gap-0 items-start">
            {/* Left - Text Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="text-center lg:text-left py-6 sm:py-8 lg:py-16 order-1 min-w-0 px-1"
            >
              {/* Logo */}
              <img src={LOGO_HERO} alt="DEA — Your AI Ambition Coach" className="h-14 sm:h-16 lg:h-20 w-auto object-contain mb-5 mx-auto lg:mx-0" data-testid="hero-logo" />
              
              
              {/* Headline */}
              <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#726158] leading-tight mb-6">
                Your Goals. Your Habits. Your <span className="text-[#CAAB84]">Becoming.</span>
              </h1>
              
              {/* Subheadline */}
              <p className="text-base sm:text-lg text-[#AA9283] max-w-xl mx-auto lg:mx-0 mb-8">
                Dea is the coach who shows up every single day, helping you build your confidence, your habits, and your business at the same time. So becoming <strong className="text-[#726158]">her</strong> stops feeling like a someday dream and starts feeling like today's plan.
              </p>
              
              {/* CTA — lifetime members see a continue-to-chat button; everyone else sees the lifetime CTA */}
              {isLifetimeMember ? (
                <Link to="/chat" className="inline-block max-w-full">
                  <Button className="btn-gold text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 max-w-full" data-testid="hero-cta-btn">
                    Continue with Dea
                    <ArrowRight className="ml-2 h-5 w-5 shrink-0" />
                  </Button>
                </Link>
              ) : (
                <Link to="/checkout?plan=lifetime" className="inline-block max-w-full">
                  <Button className="btn-gold text-base sm:text-lg px-6 sm:px-8 py-5 sm:py-6 max-w-full" data-testid="hero-cta-btn">
                    Start Your 90-Day Journey
                    <ArrowRight className="ml-2 h-5 w-5 shrink-0" />
                  </Button>
                </Link>
              )}
              <p className="text-xs text-[#AA9283] mt-3 lg:text-left text-center" data-testid="hero-pricing-note">
                {isLifetimeMember ? 'You\u2019re already a Lifetime Member \u2713' : `Lifetime access \u00b7 $${pricing.lifetime_price} one-time${pricing.is_early_access ? ` (normally $${pricing.lifetime_regular_price})` : ''}`}
              </p>
            </motion.div>

            {/* Right - Deon's Photo */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.15 }}
              className="relative flex justify-center lg:justify-end min-w-0 order-2"
            >
              <div className="relative w-full max-w-[280px] sm:max-w-sm lg:max-w-lg mx-auto lg:mx-0">
                {/* Gradient blend on left edge for desktop */}
                <div className="hidden lg:block absolute inset-y-0 left-0 w-24 z-10" style={{ background: 'linear-gradient(to right, #FFFCF7, transparent)' }} />
                <img
                  src="/images/deon-hero.png"
                  alt="Deon Stokes — The Ambition Brand"
                  className="w-full h-auto object-cover rounded-t-3xl lg:rounded-none"
                  data-testid="hero-photo"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Planner Bonus Promo — Limited time: get the 2026 Digital Ambition Planner FREE for new monthly members */}

      {/* Lifetime Access to Dea */}
      <LifetimeAccessSection isLifetimeMember={isLifetimeMember} />

      {/* Decorative gold divider */}
      <div className="bg-[#FFFCF7]">
        <div
          className="w-[40px] md:w-[60px] h-[1px] bg-[#B8956A] mx-auto my-[20px]"
          data-testid="landing-gold-divider"
        />
      </div>

      {/* Is This You? - Interactive Section (FIRST after hero) */}
      <IsThisYouSection isLifetimeMember={isLifetimeMember} />

      {/* The Ambition Method */}
      <section className="py-24 bg-[#EAD8C2]/20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-4">
              The Ambition <span className="text-[#CAAB84]">Method</span>
            </h2>
            <p className="text-[#AA9283] max-w-2xl mx-auto text-base sm:text-lg">
              Dea coaches you across all 5 areas at the same time, so you grow from the inside out while you build. You don't do one and then the other. You do them together.
            </p>
          </motion.div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4 sm:gap-6">
            {[
              { icon: Target, title: 'Clarity', desc: 'Your goals, vision and why', color: '#CAAB84' },
              { icon: RotateCcw, title: 'Habits', desc: 'Routines that actually stick', color: '#87A6A7' },
              { icon: Brain, title: 'Mindset', desc: 'Confidence to step up', color: '#AA9283' },
              { icon: TrendingUp, title: 'Strategy', desc: 'Business, content and income', color: '#726158' },
              { icon: Heart, title: 'Well-being', desc: 'Rest, energy and cycle syncing', color: '#B1897C' },
            ].map((pillar, i) => (
              <motion.div
                key={pillar.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.08 }}
                className="glass-card rounded-2xl p-5 sm:p-6 text-center card-hover"
              >
                <div
                  className="w-14 h-14 rounded-2xl flex items-center justify-center mx-auto mb-4"
                  style={{ backgroundColor: `${pillar.color}18` }}
                >
                  <pillar.icon className="h-7 w-7" style={{ color: pillar.color }} />
                </div>
                <h3 className="font-serif text-lg sm:text-xl text-[#726158] mb-2">{pillar.title}</h3>
                <p className="text-[#AA9283] text-xs sm:text-sm leading-relaxed">{pillar.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-24 bg-[#EAD8C2]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-4">
              Real <span className="text-[#CAAB84]">Transformations</span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {testimonials.map((testimonial, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-2xl p-8 card-hover"
              >
                <div className="flex mb-4">
                  {[1, 2, 3, 4, 5].map((star) => (
                    <Sparkles key={star} className="h-4 w-4 text-[#CAAB84]" />
                  ))}
                </div>
                <p className="text-[#AA9283] mb-6 italic">"{testimonial.quote}"</p>
                <div className="flex items-center gap-3">
                  <img 
                    src={testimonial.image} 
                    alt={testimonial.author}
                    className="w-14 h-14 rounded-full object-cover border-2 border-[#EAD8C2]"
                  />
                  <p className="text-[#87A6A7] font-medium">{testimonial.author}</p>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why It's Different - Features */}
      <section className="py-24 bg-[#FFFCF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-4">
              Why It's <span className="text-[#CAAB84]">Different</span>
            </h2>
            <p className="text-[#AA9283] max-w-2xl mx-auto">
              ChatGPT can give you a plan. Dea helps you actually become <strong className="text-[#726158]">HER</strong>.
            </p>
          </motion.div>

          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {features.map((feature, i) => (
              <motion.div
                key={feature.title}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="glass-card rounded-2xl p-6 card-hover"
              >
                <div className="w-12 h-12 rounded-xl bg-[#87A6A7]/15 flex items-center justify-center mb-4">
                  <feature.icon className="h-6 w-6 text-[#87A6A7]" />
                </div>
                <h3 className="font-serif text-xl text-[#726158] mb-2">{feature.title}</h3>
                <p className="text-[#AA9283] text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Meet Dea Section */}
      <section className="py-16 bg-[#FFFCF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left - Image of Dea */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative"
            >
              <div className="rounded-3xl shadow-xl w-full max-w-md mx-auto overflow-hidden">
                <img 
                  src={DEA_AVATAR} 
                  alt="Dea - Your AI Coach" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>

            {/* Right - Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-5 lg:flex-1 lg:max-w-lg mt-8 md:mt-0"
            >
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#726158]">
                Meet <span className="text-[#CAAB84]">Dea</span>, Your AI Ambition Coach
              </h2>
              <p className="text-[#AA9283] text-sm sm:text-base">
                Dea is trained by Deon Stokes, coach, founder, and the woman behind The Ambition Brand. With 20+ years of experience helping women transform their goals, habits, mindset, strategy, and well-being, Deon poured everything she knows into Dea.
              </p>
              <ul className="space-y-3 text-sm sm:text-base">
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0 mt-0.5" />
                  <span className="text-[#AA9283]">Clarity on your goals so you stop spinning and finally start moving</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0 mt-0.5" />
                  <span className="text-[#AA9283]">Daily habits and mindset support that make your growth sustainable</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0 mt-0.5" />
                  <span className="text-[#AA9283]">Business strategy for when you're ready to grow your income and impact</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0 mt-0.5" />
                  <span className="text-[#AA9283]">Cycle syncing to work WITH your body, not against it</span>
                </li>
                <li className="flex items-start gap-3">
                  <CheckCircle className="h-5 w-5 text-[#87A6A7] flex-shrink-0 mt-0.5" />
                  <span className="text-[#AA9283]">24/7 accountability from a coach who shows up every single day</span>
                </li>
              </ul>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Meet Deon Section */}
      <section className="py-16 bg-[#EAD8C2]/30">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="grid md:grid-cols-2 gap-8 md:gap-12 items-center">
            {/* Left - Content */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="space-y-5 lg:flex-1 lg:max-w-lg order-2 md:order-1"
            >
              <h2 className="font-serif text-2xl sm:text-3xl md:text-4xl text-[#726158] leading-tight">
                Meet <span className="text-[#CAAB84]">Deon, MBA</span> — The Face Of Ambition
              </h2>
              <div className="space-y-4 text-[#AA9283] text-sm sm:text-base">
                <p>
                  For 20 years, I've coached ambitious women through building and rebuilding their businesses.
                </p>
                <p>
                  And the #1 thing I heard over and over?
                </p>
                <p className="text-[#726158] font-medium italic text-base sm:text-lg">
                  "I just wish you could tell me what to do every day so I don't have to guess."
                </p>
                <p>
                  I wanted to be that coach. But I couldn't show up for everyone, every single day.
                </p>
                <p className="font-medium text-[#726158]">
                  So I built Dea.
                </p>
                <p>
                  I've trained her on everything I know. She checks in daily. She adjusts your plan when life happens. And she never lets you fall through the cracks.
                </p>
                <p className="text-[#726158]">
                  She's the coach I always wished I could be — and now she's yours.
                </p>
              </div>
              
              {/* CTA Button after Meet Deon — lifetime members continue to chat */}
              {isLifetimeMember ? (
                <Link to="/chat">
                  <Button className="btn-gold text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-5 mt-4 w-full sm:w-auto">
                    Continue with Dea
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              ) : (
                <Link to="/checkout?plan=lifetime">
                  <Button className="btn-gold text-base sm:text-lg px-6 sm:px-8 py-4 sm:py-5 mt-4 w-full sm:w-auto">
                    Get Lifetime Access
                    <ArrowRight className="ml-2 h-5 w-5" />
                  </Button>
                </Link>
              )}
            </motion.div>

            {/* Right - Image of Deon */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              className="relative order-1 md:order-2"
            >
              <div className="rounded-3xl shadow-xl w-full max-w-md mx-auto overflow-hidden">
                <img 
                  src={DEON_AVATAR} 
                  alt="Deon Stokes, MBA - The Face of Ambition" 
                  className="w-full h-auto object-cover"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Ready to Transform CTA with Pricing */}
      <section className="py-24 bg-[#EAD8C2]/30">
        <div className="max-w-4xl mx-auto px-4 sm:px-6">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-3xl p-12 text-center relative overflow-hidden"
            style={{
              background: 'linear-gradient(135deg, #FFFCF7 0%, #EAD8C2 100%)'
            }}
          >
            <div className="relative z-10">
              <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-4">
                {isLifetimeMember ? "You're In, Lifetime Member" : "Ready to Become Her?"}
              </h2>
              <p className="text-[#AA9283] mb-8 max-w-lg mx-auto">
                {isLifetimeMember
                  ? "Your Founding Member access is yours forever. Just keep showing up — Dea is waiting for you."
                  : "Join the women who are becoming who they're meant to be, one day at a time."}
              </p>
              
              {/* Pricing — hidden for lifetime members */}
              {!isLifetimeMember && (
                <div className="mb-8">
                  <p className="font-serif text-3xl sm:text-4xl text-[#726158] font-bold mb-2" data-testid="final-cta-price">
                    Lifetime Access. <span className="text-[#CAAB84]">${pricing.lifetime_price} one-time</span>{pricing.is_early_access && <span className="text-[#AA9283] text-xl sm:text-2xl font-normal"> (normally ${pricing.lifetime_regular_price})</span>}
                  </p>
                  <p className="text-[#AA9283]">
                    One-time payment. Yours for life.
                  </p>
                </div>
              )}
              
              {isLifetimeMember ? (
                <Link to="/chat" className="inline-block max-w-full">
                  <Button className="btn-gold text-lg px-6 sm:px-10 py-6 max-w-full" data-testid="cta-signup-btn">
                    Continue with Dea
                    <ArrowRight className="ml-2 h-5 w-5 shrink-0" />
                  </Button>
                </Link>
              ) : (
                <Link to="/checkout?plan=lifetime" className="inline-block max-w-full">
                  <Button className="btn-gold text-lg px-6 sm:px-10 py-6 max-w-full" data-testid="cta-signup-btn">
                    Get Lifetime Access
                    <ArrowRight className="ml-2 h-5 w-5 shrink-0" />
                  </Button>
                </Link>
              )}
            </div>
          </motion.div>
        </div>
      </section>

      {/* FAQ Section */}
      <section className="py-24 bg-[#FFFCF7]">
        <div className="max-w-3xl mx-auto px-4 sm:px-6">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="font-serif text-3xl sm:text-4xl text-[#726158] mb-4">
              Frequently Asked <span className="text-[#CAAB84]">Questions</span>
            </h2>
          </motion.div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="glass-card rounded-2xl p-6"
          >
            {faqItems.map((item, index) => (
              <FAQItem key={index} question={item.question} answer={item.answer} />
            ))}
          </motion.div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 border-t border-[#EAD8C2] bg-[#FFFCF7]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="flex flex-col items-center md:items-start gap-1">
              <img src={LOGO_NAV} alt="Dea" className="h-10 w-auto" />
            </div>
            <div className="flex items-center gap-4">
              <Link to="/privacy" className="text-[#B1897C] text-sm hover:text-[#726158] transition-colors">
                Privacy Policy
              </Link>
              <span className="text-[#B1897C]">|</span>
              <Link to="/terms" className="text-[#B1897C] text-sm hover:text-[#726158] transition-colors">
                Terms of Service
              </Link>
              <span className="text-[#B1897C]">|</span>
              <p className="text-[#B1897C] text-sm">
                © 2026 The Ambition Brand. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>
    </>
  );
}
